<template>
  <div class="about-page">
    <!-- 主要内容区域 -->
    <main style="margin-top: 70px;">
      <!-- 搜索区块 -->
      <section class="search-section">
        <div class="container">
          <div class="search-container">
            <div class="search-box" :class="{ 'focus': searchFocused }">
              <i class="fas fa-search search-icon"></i>
              <input 
                type="text" 
                v-model="searchQuery"
                @focus="searchFocused = true"
                @blur="searchFocused = false"
                @keypress.enter="performSearch"
                placeholder="搜索非遗项目、类别、地区..." 
                class="search-input"
              >
              <button class="search-btn" @click="performSearch">
                <i class="fas fa-arrow-right"></i>
              </button>
            </div>
            
            <!-- 搜索建议 -->
            <div 
              class="search-suggestions" 
              v-show="showSuggestions && searchSuggestions.length > 0"
            >
              <div 
                v-for="suggestion in searchSuggestions" 
                :key="suggestion"
                class="suggestion-item"
                @click="selectSuggestion(suggestion)"
              >
                {{ suggestion }}
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 非遗分类展示区块 -->
      <section class="heritage-categories" v-show="!showSearchResults">
        <div class="container">
          <div class="categories-wrapper">
            <div 
              v-for="(category, index) in mainData" 
              :key="category.id"
              class="category-section"
              :data-category="category.categoryName"
              :style="{ animationDelay: `${index * 0.1}s` }"
            >
              <h3 class="category-title" :class="category.categoryClass">
                {{ category.categoryName }}
              </h3>
              <div class="heritage-grid">
                <div 
                  v-for="item in category.items" 
                  :key="item.id"
                  class="heritage-card"
                  :data-keywords="item.keywords"
                  @click="handleItemClick(item)"
                  :style="{ cursor: item.clickAction ? 'pointer' : 'default' }"
                >
                  <div class="card-image">
                    <img 
                      :src="item.image" 
                      :alt="item.title" 
                      class="heritage-img"
                      @error="handleImageError"
                    >
                  </div>
                  <div class="card-content">
                    <h4>{{ item.title }}</h4>
                    <p>{{ item.description }}</p>
                    <div class="card-tags">
                      <span 
                        v-for="tag in item.tags" 
                        :key="tag"
                        class="tag"
                      >
                        {{ tag }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 显示更多按钮 -->
      <section class="show-more-section" v-show="!showSearchResults">
        <div class="container">
          <button 
            class="show-more-btn"
            :class="{ expanded: showMoreContent }"
            @click="toggleMoreContent"
          >
            <span class="btn-text">
              {{ showMoreContent ? '收起内容' : '显示更多内容' }}
            </span>
            <i 
              class="fas fa-chevron-down btn-icon"
              :style="{ transform: showMoreContent ? 'rotate(180deg)' : 'rotate(0deg)' }"
            ></i>
          </button>
        </div>
      </section>

      <!-- 更多内容模块 -->
      <section 
        class="more-content-section"
        :class="{ active: showMoreContent }"
        v-show="showMoreContent"
      >
        <div class="container">
          <h2 class="section-title-alt">
            <i class="fas fa-list-ul"></i>
            深度探索非遗分类
          </h2>
          
          <div class="horizontal-scroll-container">
            <!-- 滚动提示 -->
            <div class="scroll-hint" v-show="showScrollHint">
              <i class="fas fa-hand-point-right"></i>
              <span>每个分类内可左右滑动查看更多项目</span>
              <i class="fas fa-hand-point-left"></i>
            </div>
            
            <div class="heritage-list-layout">
              <div 
                v-for="(category, index) in extendedData" 
                :key="category.id"
                class="heritage-list-category"
                :style="{ animationDelay: `${(index + 1) * 0.1}s` }"
              >
                <div class="category-header-list">
                  <div class="category-icon-wrapper" :class="category.categoryClass">
                    <i :class="category.categoryIcon"></i>
                  </div>
                  <div class="category-info">
                    <h3>{{ category.categoryName }}</h3>
                    <p>{{ category.description }}</p>
                  </div>
                  <div class="category-count">
                    <span>{{ category.itemCount }}项</span>
                  </div>
                </div>
                <div class="heritage-list-items">
                  <div 
                    v-for="item in category.items" 
                    :key="item.id"
                    class="heritage-list-item"
                    @click="handleItemClick(item)"
                    :style="{ cursor: item.clickAction ? 'pointer' : 'default' }"
                  >
                    <div class="item-thumbnail">
                      <img 
                        :src="item.image" 
                        :alt="item.title"
                        @error="handleImageError"
                      >
                      <div class="item-overlay">
                        <i class="fas fa-eye"></i>
                      </div>
                    </div>
                    <div class="item-details">
                      <h4>{{ item.title }}</h4>
                      <p>{{ item.description }}</p>
                      <div class="item-meta">
                        <span 
                          v-for="tag in item.metaTags" 
                          :key="tag"
                          class="meta-tag"
                        >
                          {{ tag }}
                        </span>
                        <span class="meta-date">{{ item.metaDate }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 搜索结果区块 -->
      <section class="search-results" v-show="showSearchResults">
        <div class="container">
          <h2 class="section-title">搜索结果</h2>
          <div v-if="searchResults.length > 0" class="heritage-grid">
            <div 
              v-for="result in searchResults" 
              :key="result.id"
              class="heritage-card"
              @click="handleItemClick(result)"
              :style="{ cursor: result.clickAction ? 'pointer' : 'default' }"
            >
              <div class="card-image">
                <img 
                  :src="result.image" 
                  :alt="result.title" 
                  class="heritage-img"
                  @error="handleImageError"
                >
              </div>
              <div class="card-content">
                <h4>{{ result.title }}</h4>
                <p>{{ result.description }}</p>
                <div class="card-tags">
                  <span class="tag category-tag">{{ result.category }}</span>
                  <span 
                    v-for="tag in (result.tags || result.metaTags || [])" 
                    :key="tag"
                    class="tag"
                  >
                    {{ tag }}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="no-results">
            <i class="fas fa-search"></i>
            <p>未找到相关结果，请尝试其他关键词</p>
          </div>
        </div>
      </section>
    </main>

    <!-- 页面底部 -->
    <footer class="main-footer">
      <div class="container">
        <div class="footer-content">
          <div class="footer-section">
            <h4>关于我们</h4>
            <p>致力于保护和传承中华民族非物质文化遗产</p>
          </div>
          <div class="footer-section">
            <h4>快速链接</h4>
            <ul>
              <li><router-link to="/about">认识非遗</router-link></li>
              <li><router-link to="/explore">走近非遗</router-link></li>
              <li><router-link to="/heritage">传习非遗</router-link></li>
            </ul>
          </div>
          <div class="footer-section">
            <h4>联系我们</h4>
            <p>邮箱: info@heritage.cn</p>
            <p>电话: 010-12345678</p>
          </div>
        </div>
        <div class="footer-bottom">
          <p>&copy; 2024 中华非遗. 保留所有权利.</p>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'AboutPage',
  
  data() {
    return {
      searchQuery: '',
      searchFocused: false,
      showSuggestions: false,
      showMoreContent: false,
      showSearchResults: false,
      showScrollHint: true,
      searchResults: []
    }
  },
  
  computed: {
    ...mapGetters('heritage', [
      'getMainData',
      'getExtendedData', 
      'getSearchSuggestions'
    ]),
    
    /**
     * 主要数据
     */
    mainData() {
      return this.getMainData
    },
    
    /**
     * 扩展数据
     */
    extendedData() {
      return this.getExtendedData
    },
    
    /**
     * 搜索建议
     */
    searchSuggestions() {
      if (!this.searchQuery.trim()) return []
      
      return this.getSearchSuggestions.filter(suggestion =>
        suggestion.toLowerCase().includes(this.searchQuery.toLowerCase())
      ).slice(0, 5)
    }
  },
  
  mounted() {
    this.initScrollEffects()
    this.autoHideScrollHint()
  },
  
  beforeDestroy() {
    this.cleanupEventListeners()
  },
  
  methods: {
    ...mapActions('heritage', [
      'searchHeritage'
    ]),
    
    /**
     * 执行搜索
     */
    async performSearch() {
      if (!this.searchQuery.trim()) {
        this.showSearchResults = false
        return
      }
      
      try {
        const results = await this.searchHeritage(this.searchQuery)
        this.searchResults = results
        this.showSearchResults = true
        this.showSuggestions = false
        
        // 滚动到搜索结果
        this.$nextTick(() => {
          const resultsSection = document.querySelector('.search-results')
          if (resultsSection) {
            resultsSection.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'start' 
            })
          }
        })
      } catch (error) {
        console.error('搜索失败:', error)
        this.searchResults = []
        this.showSearchResults = true
      }
    },
    
    /**
     * 选择搜索建议
     */
    selectSuggestion(suggestion) {
      this.searchQuery = suggestion
      this.showSuggestions = false
      this.performSearch()
    },
    
    /**
     * 切换更多内容显示
     */
    toggleMoreContent() {
      this.showMoreContent = !this.showMoreContent
      
      if (this.showMoreContent) {
        this.$nextTick(() => {
          const moreSection = document.querySelector('.more-content-section')
          if (moreSection) {
            setTimeout(() => {
              moreSection.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
              })
            }, 100)
          }
        })
      } else {
        const showMoreBtn = document.querySelector('.show-more-btn')
        if (showMoreBtn) {
          showMoreBtn.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          })
        }
      }
    },
    
    /**
     * 处理项目点击
     */
    handleItemClick(item) {
      if (item.clickAction) {
        if (item.clickAction.startsWith('/')) {
          this.$router.push(item.clickAction)
        } else {
          window.location.href = item.clickAction
        }
      }
    },
    
    /**
     * 处理图片加载错误
     */
    handleImageError(event) {
      event.target.src = 'img/default-heritage.jpg'
    },
    
    /**
     * 初始化滚动效果
     */
    initScrollEffects() {
      this.handleScroll = () => {
        this.animateOnScroll()
      }
      
      window.addEventListener('scroll', this.handleScroll, { passive: true })
    },
    
    /**
     * 滚动动画
     */
    animateOnScroll() {
      const elements = document.querySelectorAll('.heritage-card, .heritage-list-item')
      
      elements.forEach(element => {
        const elementTop = element.getBoundingClientRect().top
        const elementVisible = 150
        
        if (elementTop < window.innerHeight - elementVisible) {
          element.classList.add('fade-in')
        }
      })
    },
    
    /**
     * 自动隐藏滚动提示
     */
    autoHideScrollHint() {
      setTimeout(() => {
        this.showScrollHint = false
      }, 5000)
    },
    
    /**
     * 清理事件监听器
     */
    cleanupEventListeners() {
      if (this.handleScroll) {
        window.removeEventListener('scroll', this.handleScroll)
      }
    }
  },
  
  watch: {
    /**
     * 监听搜索查询变化
     */
    searchQuery(newVal) {
      if (newVal.trim()) {
        this.showSuggestions = true
      } else {
        this.showSuggestions = false
        this.showSearchResults = false
      }
    },
    
    /**
     * 监听路由变化，重置搜索状态
     */
    '$route'() {
      this.searchQuery = ''
      this.showSearchResults = false
      this.showSuggestions = false
    }
  }
}
</script>

<style scoped>
.about-page {
  width: 100%;
  min-height: 100vh;
}

.search-box.focus {
  box-shadow: 0 6px 24px rgba(139, 157, 195, 0.2);
  border-color: var(--morandi-blue);
}

.search-suggestions {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid var(--morandi-border);
  border-radius: 0 0 12px 12px;
  box-shadow: var(--morandi-shadow);
  z-index: 10;
  max-height: 200px;
  overflow-y: auto;
}

.suggestion-item {
  padding: 12px 16px;
  border-bottom: 1px solid var(--morandi-border);
  cursor: pointer;
  transition: all 0.2s ease;
}

.suggestion-item:hover {
  background-color: var(--morandi-gray-light);
  color: var(--primary-color);
}

.suggestion-item:last-child {
  border-bottom: none;
}

.category-title {
  position: relative;
  transition: all 0.3s ease;
}

.category-title:hover {
  transform: translateY(-2px);
}

.heritage-card {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.heritage-card:hover {
  transform: translateY(-8px);
  box-shadow: var(--morandi-shadow-lg);
}

.more-content-section {
  opacity: 0;
  transform: translateY(30px);
  transition: all 0.5s ease;
}

.more-content-section.active {
  opacity: 1;
  transform: translateY(0);
}

.scroll-hint {
  transition: opacity 0.5s ease;
}

.category-tag {
  background: linear-gradient(135deg, var(--primary-color), var(--secondary-color));
  color: white;
  font-weight: 600;
}

.heritage-list-item {
  transition: all 0.3s ease;
}

.heritage-list-item:hover {
  transform: translateY(-4px);
  box-shadow: var(--morandi-shadow);
}

.heritage-list-item:hover .item-overlay {
  opacity: 1;
}

@media (max-width: 768px) {
  .search-container {
    padding: 0 16px;
  }
  
  .search-box {
    flex-direction: column;
    gap: 8px;
  }
  
  .heritage-grid {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1rem;
  }
  
  .heritage-list-layout {
    padding: 0 16px;
  }
  
  .scroll-hint {
    padding: 8px 16px;
    font-size: 0.9rem;
  }
}

@media (max-width: 480px) {
  .category-title {
    font-size: 1.3rem;
  }
  
  .heritage-card {
    margin-bottom: 1rem;
  }
  
  .card-content h4 {
    font-size: 1.1rem;
  }
  
  .card-content p {
    font-size: 0.9rem;
  }
}
</style> 